import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface ContactForm {
  email: string;
  subject: string;
  message: string;
}

@Component({
  selector: 'zero-waste-contact-us',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <section id="contact-us" class="contact-section">
      <div class="container">
        <h2 class="section-title">Contactez-nous</h2>
        <p class="section-description">
          Vous avez des questions ? N'hésitez pas à nous contacter.
        </p>

        <div class="contact-form-container">
          <form (ngSubmit)="onSubmit()" #contactForm="ngForm">
            <div class="form-group">
              <label for="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                [(ngModel)]="formData.email"
                required
                email
                #email="ngModel"
                [class.is-invalid]="email.invalid && email.touched"
              />
              <div class="error-message" *ngIf="email.invalid && email.touched">
                Veuillez entrer une adresse email valide
              </div>
            </div>

            <div class="form-group">
              <label for="subject">Sujet</label>
              <input
                type="text"
                id="subject"
                name="subject"
                [(ngModel)]="formData.subject"
                required
                #subject="ngModel"
                [class.is-invalid]="subject.invalid && subject.touched"
              />
              <div
                class="error-message"
                *ngIf="subject.invalid && subject.touched"
              >
                Le sujet est requis
              </div>
            </div>

            <div class="form-group">
              <label for="message">Message</label>
              <textarea
                id="message"
                name="message"
                [(ngModel)]="formData.message"
                required
                #message="ngModel"
                [class.is-invalid]="message.invalid && message.touched"
              ></textarea>
              <div
                class="error-message"
                *ngIf="message.invalid && message.touched"
              >
                Le message est requis
              </div>
            </div>

            <button
              type="submit"
              class="submit-button"
              [disabled]="contactForm.invalid || isLoading"
            >
              <span *ngIf="!isLoading">Envoyer</span>
              <span *ngIf="isLoading" class="loading-spinner"></span>
            </button>

            <div *ngIf="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            <div *ngIf="successMessage" class="success-message">
              {{ successMessage }}
            </div>
          </form>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .contact-section {
        padding: 80px 0;
        background-color: var(--light-color);
      }

      .section-title {
        text-align: center;
        color: var(--dark-color);
        margin-bottom: 20px;
      }

      .section-description {
        text-align: center;
        color: var(--text-color);
        margin-bottom: 40px;
      }

      .contact-form-container {
        max-width: 600px;
        margin: 0 auto;
        background-color: white;
        padding: 30px;
        border-radius: 10px;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
      }

      .form-group {
        margin-bottom: 20px;
      }

      label {
        display: block;
        margin-bottom: 8px;
        color: var(--dark-color);
        font-weight: 500;
      }

      input,
      textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid #ddd;
        border-radius: 5px;
        font-size: 16px;
        transition: border-color 0.3s ease;

        &:focus {
          outline: none;
          border-color: var(--primary-color);
        }

        &.is-invalid {
          border-color: var(--danger-color);
        }
      }

      textarea {
        min-height: 150px;
        resize: vertical;
      }

      .error-message {
        color: var(--danger-color);
        font-size: 14px;
        margin-top: 5px;
      }

      .success-message {
        color: var(--success-color);
        font-size: 14px;
        margin-top: 5px;
        text-align: center;
      }

      .submit-button {
        width: 100%;
        padding: 14px;
        background-color: var(--primary-color);
        color: white;
        border: none;
        border-radius: 5px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.3s ease;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;

        &:hover {
          background-color: var(--primary-dark-color);
        }

        &:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
      }

      .loading-spinner {
        width: 20px;
        height: 20px;
        border: 3px solid #ffffff;
        border-radius: 50%;
        border-top-color: transparent;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (max-width: 768px) {
        .contact-form-container {
          padding: 20px;
        }
      }
    `,
  ],
})
export class ContactUsComponent {
  formData: ContactForm = {
    email: '',
    subject: '',
    message: '',
  };

  isLoading = false;
  errorMessage = '';
  successMessage = '';

  constructor(private http: HttpClient) {}

  onSubmit() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post('/api/public/contact-messages', this.formData).subscribe({
      next: () => {
        this.successMessage = 'Votre message a été envoyé avec succès !';
        this.formData = {
          email: '',
          subject: '',
          message: '',
        };
      },
      error: (error) => {
        this.errorMessage =
          "Une erreur s'est produite lors de l'envoi du message. Veuillez réessayer.";
        console.error('Error sending message:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }
}
